import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { WorkspaceCardData } from "../components/Interfaces/WorkspaceCard";
import WorkspaceCard from "../components/WorkspaceCard";
import FeaturedWorkspaceCard from "../components/FeaturedWorkspaceCard";
import WorkspaceMap from "../components/GoogleMap";
import { useJsApiLoader } from "@react-google-maps/api";
import axios from "axios";
import GoogleSearchBox from "../components/GoogleSearchBox";
import { AddressComponents } from "../components/AddressComponents";
import { toast } from "react-toastify";
import { UserContext } from "../components/UserContext";
import AuthModal from "../components/AuthModal";

interface FeaturedWorkspacesRequest {
  location?: string;
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  limit?: number;
}

const libraries: ("places" | "geocoding" | "marker")[] = [
  "places",
  "geocoding",
  "marker",
];

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const getAddedLabel = (createdAt?: string): string => {
  if (!createdAt) return "Just Added";
  const now = new Date();
  const created = new Date(createdAt);
  const diffMs = now.getTime() - created.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours < 1) return "Just Added";
  if (diffHours < 24) return "Added Today";
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return `Added ${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
};

function IndexPage() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [featuredWorkspaces, setFeaturedWorkspaces] = useState<
    WorkspaceCardData[]
  >([]);
  const [allWorkspaces, setAllWorkspaces] = useState<WorkspaceCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [searchLocation, setSearchLocation] = useState("");

  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries,
    version: "weekly",
  });

  useEffect(() => {
    fetchFeaturedWorkspaces();
    fetchAllWorkspaces();
  }, []);

  const fetchFeaturedWorkspaces = async () => {
    try {
      setLoading(true);
      const api = axios.create({ baseURL: "/" });
      const payload: FeaturedWorkspacesRequest = { radiusKm: 50, limit: 12 };
      const response = await api.post(
        "/api/Workspace/getFeaturedWorkspaces",
        payload,
      );
      const data = response.data;
      setFeaturedWorkspaces(
        Array.isArray(data) ? data : data?.workspaces || [],
      );
      setError(null);
    } catch (err) {
      console.error("Error fetching featured workspaces:", err);
      setError("Failed to load featured workspaces");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllWorkspaces = async () => {
    try {
      const api = axios.create({ baseURL: "/" });
      const response = await api.post("/api/Workspace/listWorkspaces", {
        location: "",
        moveInDate: null,
        capacity: null,
        userId: null,
        page: 1,
        pageSize: 1000,
      });
      const data = response.data;
      setAllWorkspaces(Array.isArray(data) ? data : data?.workspaces || []);
    } catch (err) {
      console.error("Error fetching all workspaces:", err);
    }
  };

  const handleContactFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const api = axios.create({ baseURL: "/" });
      const response = await api.post("/api/Contact/submit", {
        firstName: contactForm.name.split(" ")[0] || "",
        lastName: contactForm.name.split(" ").slice(1).join(" ") || "",
        email: contactForm.email,
        mobile: contactForm.phone,
        service: contactForm.service,
        message: contactForm.message,
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setContactForm({
          name: "",
          email: "",
          phone: "",
          service: "",
          message: "",
        });
      } else {
        toast.error(
          response.data.errorMessage ||
            "Failed to send your message. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error(
        "An error occurred while sending your message. Please try again later.",
      );
    }
  };

  const handleLocationSelect = (address: AddressComponents) => {
    setSearchLocation(address.fullAddress || "");
  };

  const handleSearch = () => {
    navigate("/workspaces", {
      state: { searchLocation, fromHomePage: true },
    });
  };

  // Derived values
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const recentWorkspaces = allWorkspaces
    .filter((w) => w.createdAt && new Date(w.createdAt) >= thirtyDaysAgo)
    .slice(0, 8);

  const weeklyPrices = allWorkspaces
    .map((w) => w.pricePerWeek)
    .filter((p): p is number => !!p && p > 0);
  const annualPrices = allWorkspaces
    .map((w) => w.pricePerYear)
    .filter((p) => p > 0);
  const lowestWeeklyPrice =
    weeklyPrices.length > 0
      ? Math.min(...weeklyPrices)
      : annualPrices.length > 0
        ? Math.min(...annualPrices) / 52
        : null;

  if (loading) {
    return (
      <div style={{ backgroundColor: "var(--background)" }}>
        <div className="max-w-[1600px] mx-auto flex items-start">
          <div className="hidden xl:block w-[12.5%] flex-shrink-0" />
          <div className="flex-1 min-w-0 px-4 py-8">
            <div className="animate-pulse">
              <div className="h-96 bg-gray-300 rounded-lg mb-8"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(8)].map((_, index) => (
                  <div key={index} className="bg-gray-300 rounded-lg h-64"></div>
                ))}
              </div>
            </div>
          </div>
          <div className="hidden xl:block w-[12.5%] flex-shrink-0" />
        </div>
      </div>
    );
  }

  const AdColumn = () => (
    <aside className="hidden xl:block w-[12.5%] flex-shrink-0" />
  );

  return (
    <div style={{ backgroundColor: "var(--background)" }}>
      <div className="max-w-[1600px] mx-auto flex items-start">
        <AdColumn />
        <main className="flex-1 min-w-0 px-4 py-8">
      {/* Platform Introduction + Dual CTA Section */}
      <div className="mb-16">
        {/* Platform description */}
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-[var(--primary)] text-[var(--secondary)] text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wider">
            <svg
              className="w-3.5 h-3.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1h-2a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                clipRule="evenodd"
              />
            </svg>
            Australia's commercial real estate marketplace
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            Skip the middleman.{" "}
            <span className="text-[var(--secondary)]">Rent direct.</span>
          </h2>
          <p className="text-lg text-gray-500 leading-relaxed">
            AirArrange connects businesses searching for commercial space
            directly with property owners across Australia. We cut through the
            complexity of traditional real estate — no agents, no inflated
            prices, just a simple and transparent path from search to signed
            lease.
          </p>
        </div>

        {/* Two CTA cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Business / User CTA */}
          <div className="relative overflow-hidden rounded-3xl bg-[var(--secondary)] p-8 md:p-10 flex flex-col justify-between min-h-72">
            <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-white/[0.04]" />
            <div className="absolute -bottom-8 -left-6 w-40 h-40 rounded-full bg-white/[0.04]" />
            <div className="relative">
              <div className="inline-flex w-12 h-12 bg-[var(--primary)] rounded-2xl items-center justify-center mb-5">
                <svg
                  className="w-6 h-6 text-[var(--secondary)]"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M7 2a2 2 0 00-2 2v1H4a2 2 0 00-2 2v13a2 2 0 002 2h16a2 2 0 002-2V7a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7zm0 2h10v1H7V4zM4 7h16v13H4V7zm4 3a1 1 0 011-1h6a1 1 0 010 2H9a1 1 0 01-1-1zm0 4a1 1 0 011-1h6a1 1 0 010 2H9a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="text-[var(--primary)] text-xs font-semibold uppercase tracking-wider mb-2">
                For Businesses
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                Find your perfect workspace
              </h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Browse offices, retail spaces, warehouses and more — all listed
                directly by their owners. Filter by location, size and budget,
                then book your workspace in minutes. No agent markups, no
                inflated prices. Just the space your business needs, at a price
                that makes sense.
              </p>
            </div>
            <div className="relative mt-6">
              <Link
                to="/workspaces"
                className="inline-flex items-center gap-2 bg-[var(--primary)] text-[var(--secondary)] font-semibold px-6 py-3 rounded-xl hover:bg-white transition-colors text-sm"
              >
                Search for your workspace
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>
            </div>
          </div>

          {/* Landlord CTA */}
          <div className="relative overflow-hidden rounded-3xl bg-gray-900 p-8 md:p-10 flex flex-col justify-between min-h-72">
            <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[var(--tertiary)]/[0.06]" />
            <div className="absolute -bottom-8 -left-6 w-40 h-40 rounded-full bg-[var(--tertiary)]/[0.06]" />
            <div className="relative">
              <div className="inline-flex w-12 h-12 bg-[var(--tertiary)] rounded-2xl items-center justify-center mb-5">
                <svg
                  className="w-6 h-6 text-gray-900"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <div className="text-[var(--tertiary)] text-xs font-semibold uppercase tracking-wider mb-2">
                For Landlords
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                Own commercial property?
              </h3>
              <p className="text-white/70 text-sm leading-relaxed">
                AirArrange is built for commercial property owners who want to
                take back control. List all your properties, manage them from
                one clean dashboard, and communicate directly with prospective
                tenants — no agent sitting in the middle inflating the cost. You
                get genuine client relationships, zero agency fees eating into
                your returns, and only a small transparent platform fee from us.
                Less admin, less headache, more growth.
              </p>
            </div>
            <div className="relative mt-6">
              {user ? (
                <Link
                  to="/listyourworkspace"
                  className="inline-flex items-center gap-2 bg-[var(--tertiary)] text-gray-900 font-semibold px-6 py-3 rounded-xl hover:bg-white transition-colors text-sm"
                >
                  List your property
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              ) : (
                <button
                  onClick={() => setIsAuthModalOpen(true)}
                  className="inline-flex items-center gap-2 bg-[var(--tertiary)] text-gray-900 font-semibold px-6 py-3 rounded-xl hover:bg-white transition-colors text-sm"
                >
                  List your property
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Featured Workspaces */}
      {!error && featuredWorkspaces.length > 0 && (
        <div className="mb-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Featured Workspaces
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl">
              Discover the highest-rated and most recommended workspaces,
              carefully selected for their exceptional quality and customer
              satisfaction.
            </p>
          </div>

          {/* Custom Featured Grid Layout - 5 columns x 7 rows */}
          <div
            className="grid gap-4 mb-8"
            style={{
              gridTemplateColumns: "repeat(5, 1fr)",
              gridTemplateRows: "repeat(7, 120px)",
              minHeight: "840px",
            }}
          >
            {/* Card 1: 3x5 */}
            {featuredWorkspaces[0] && (
              <div
                className="relative"
                style={{ gridColumn: "span 3", gridRow: "span 5" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <FeaturedWorkspaceCard {...featuredWorkspaces[0]} />
                </div>
              </div>
            )}
            {featuredWorkspaces[1] && (
              <div
                className="relative"
                style={{ gridColumn: "span 1", gridRow: "span 3" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-gray-400 to-gray-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <FeaturedWorkspaceCard {...featuredWorkspaces[1]} />
                </div>
              </div>
            )}
            {featuredWorkspaces[2] && (
              <div
                className="relative"
                style={{ gridColumn: "span 1", gridRow: "span 3" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <FeaturedWorkspaceCard {...featuredWorkspaces[2]} />
                </div>
              </div>
            )}
            {featuredWorkspaces[3] && (
              <div
                className="relative"
                style={{ gridColumn: "span 1", gridRow: "span 2" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <FeaturedWorkspaceCard {...featuredWorkspaces[3]} />
                </div>
              </div>
            )}
            {featuredWorkspaces[4] && (
              <div
                className="relative"
                style={{ gridColumn: "span 1", gridRow: "span 2" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <FeaturedWorkspaceCard {...featuredWorkspaces[4]} />
                </div>
              </div>
            )}
            {featuredWorkspaces[5] && (
              <div
                className="relative"
                style={{ gridColumn: "span 1", gridRow: "span 2" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <WorkspaceCard {...featuredWorkspaces[5]} />
                </div>
              </div>
            )}
            {featuredWorkspaces[6] && (
              <div
                className="relative"
                style={{ gridColumn: "span 1", gridRow: "span 2" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <FeaturedWorkspaceCard {...featuredWorkspaces[6]} />
                </div>
              </div>
            )}
            {featuredWorkspaces[7] && (
              <div
                className="relative"
                style={{ gridColumn: "span 1", gridRow: "span 2" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <WorkspaceCard {...featuredWorkspaces[7]} />
                </div>
              </div>
            )}
            {featuredWorkspaces[8] && (
              <div
                className="relative"
                style={{ gridColumn: "span 1", gridRow: "span 2" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <WorkspaceCard {...featuredWorkspaces[8]} />
                </div>
              </div>
            )}
            {featuredWorkspaces[9] && (
              <div
                className="relative"
                style={{ gridColumn: "span 1", gridRow: "span 2" }}
              >
                <div className="absolute top-3 left-3 z-10">
                  <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    Featured
                  </div>
                </div>
                <div className="h-full">
                  <WorkspaceCard {...featuredWorkspaces[9]} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recently Added Section */}
      {recentWorkspaces.length > 0 && (
        <div className="mb-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Recently Added
              </h2>
              <p className="text-gray-500 text-sm">
                Fresh listings added in the last 30 days
              </p>
            </div>
            <Link
              to="/workspaces"
              className="flex items-center gap-1 text-[var(--secondary)] text-sm font-medium hover:underline"
            >
              View all
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {recentWorkspaces.map((workspace) => (
              <div
                key={workspace.workspaceId}
                className="relative rounded-2xl ring-2 ring-emerald-400"
              >
                <div className="absolute -top-2.5 right-4 z-20">
                  <span className="bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm uppercase tracking-wide">
                    {getAddedLabel(workspace.createdAt)}
                  </span>
                </div>
                <WorkspaceCard {...workspace} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Google Map Section */}
      {/* Hero Search Section */}
      <div className="mb-12 max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-medium text-2xl md:text-5xl text-gray-900 mb-4">
            Search for workspaces across Australia with our interactive map
          </h1>
          <p className="text-lg text-gray-600">
            Discover amazing workspaces for your team, anywhere in Australia
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-2">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              {isGoogleMapsLoaded ? (
                <GoogleSearchBox
                  onPlaceSelected={handleLocationSelect}
                  onInputChange={(value) => setSearchLocation(value)}
                />
              ) : (
                <div className="flex flex-col w-full gap-1 px-4 py-1 rounded-2xl">
                  <div className="text-md font-semibold">Location</div>
                  <input
                    type="text"
                    placeholder="Loading search..."
                    className="w-full focus:outline-none bg-transparent placeholder-gray-500 text-sm"
                    disabled
                  />
                </div>
              )}
            </div>
            <button
              className="bg-[var(--primary)] rounded-full p-3 px-6 cursor-pointer hover:shadow-2xl hover:bg-[var(--secondary)] hover:text-white transition-colors whitespace-nowrap font-medium flex-shrink-0"
              onClick={handleSearch}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 640"
                strokeWidth={1.5}
                fill="currentColor"
                className="w-8 h-8"
              >
                <path d="M480 272C480 317.9 465.1 360.3 440 394.7L566.6 521.4C579.1 533.9 579.1 554.2 566.6 566.7C554.1 579.2 533.8 579.2 521.3 566.7L394.7 440C360.3 465.1 317.9 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272zM272 416C351.5 416 416 351.5 416 272C416 192.5 351.5 128 272 128C192.5 128 128 192.5 128 272C128 351.5 192.5 416 272 416z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div className="mb-12">
        <div className="h-[600px] rounded-lg overflow-hidden shadow-lg">
          {isGoogleMapsLoaded ? (
            <WorkspaceMap
              workspaces={allWorkspaces}
              onWorkspaceSelect={(workspace) => {
                console.log("Selected workspace:", workspace);
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-200">
              <div className="text-gray-500">Loading map...</div>
            </div>
          )}
        </div>
      </div>

      {/* Workspace Services Section */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 md:p-12 mb-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Workspace Solutions & Services
            </h2>
            <p className="text-lg text-gray-600">
              Need workspace renovations, installations, or custom solutions? We
              provide comprehensive quotes and professional services to
              transform your space.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Services Info */}
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Our Services
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Office Renovations
                    </h4>
                    <p className="text-gray-600">
                      Complete office makeovers, layout redesign, and
                      modernization projects.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Technology Installations
                    </h4>
                    <p className="text-gray-600">
                      Smart office systems, networking, audio-visual equipment,
                      and automation setup.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Custom Furniture & Fit-outs
                    </h4>
                    <p className="text-gray-600">
                      Bespoke furniture design, ergonomic solutions, and
                      complete workspace fit-outs.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      Maintenance & Support
                    </h4>
                    <p className="text-gray-600">
                      Ongoing maintenance services, emergency repairs, and
                      workspace optimization.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                Get a Quote
              </h3>
              <form onSubmit={handleContactFormSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email address"
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleContactFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                  />
                </div>
                <div>
                  <label
                    htmlFor="service"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Service Required *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={contactForm.service}
                    onChange={handleContactFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a service</option>
                    <option value="renovation">Office Renovation</option>
                    <option value="technology">Technology Installation</option>
                    <option value="furniture">
                      Custom Furniture & Fit-outs
                    </option>
                    <option value="maintenance">Maintenance & Support</option>
                    <option value="consultation">General Consultation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactFormChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe your project requirements, timeline, and any specific needs..."
                  />
                </div>
                <button type="submit" className="secondary-button w-full">
                  Send Inquiry
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Price Banner */}
      {allWorkspaces.length > 0 && lowestWeeklyPrice !== null && (
        <div className="rounded-3xl overflow-hidden bg-gradient-to-r from-[var(--secondary)] to-gray-900 px-8 py-12 md:px-14">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-1.5 bg-white/10 text-white/80 text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
                <svg
                  className="w-3 h-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
                Short-term &amp; flexible leases available
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
                Your own commercial workspace from only{" "}
                <span className="text-[var(--primary)]">
                  {formatCurrency(Math.round(lowestWeeklyPrice))}/week
                </span>
              </h3>
              <p className="text-white/60 text-sm max-w-lg">
                Book directly with landlords and move in on your timeline. No
                agents, no long-term lock-in required — just the space you need,
                when you need it.
              </p>
            </div>
            <Link
              to="/workspaces"
              className="flex-shrink-0 bg-[var(--primary)] text-[var(--secondary)] font-bold px-8 py-4 rounded-2xl hover:bg-white transition-colors text-sm whitespace-nowrap"
            >
              Browse workspaces
            </Link>
          </div>
        </div>
      )}
        </main>
        <AdColumn />
      </div>
      {isAuthModalOpen && (
        <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
      )}
    </div>
  );
}

export default IndexPage;
